import type { BillType, BlAmend, BlDraft, BlFinal, BlRelease, BlTab, Manifest, OriginalDoc, LoiTemplate } from './types';

/** Bỏ dấu + hạ chữ thường, để tìm "đại ichi" ra "DAI-ICHI". */
export const norm = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

/** Bill cần LOI đúng mẫu hãng trước khi gửi Draft (board: `LOIneed`). */
export const needsLoi = (r: Pick<BlDraft, 'type'>): boolean => r.type === 'Telex' || r.type === 'Surrender';
/** Draft bị chặn gửi vì thiếu LOI. */
export const isBlocked = (r: BlDraft): boolean => needsLoi(r) && r.loi === 'missing';

export interface BlFilter {
  q?: string;
  types?: readonly BillType[];
  cars?: readonly string[];
}

interface Searchable { job?: string; hbl?: string; cus?: string; cne?: string; no?: string; car?: string; type?: string }

/** Lọc như board: tìm nhanh (job, HBL, khách, consignee, số, hãng) AND loại bill AND hãng. */
export function filterBl<T extends Searchable>(rows: readonly T[], f: BlFilter): T[] {
  const n = norm((f.q ?? '').trim());
  return rows.filter((r) =>
    (!n || norm([r.job, r.hbl, r.cus, r.cne, r.no, r.car].join(' ')).includes(n)) &&
    (!f.types?.length || f.types.includes(r.type as BillType)) &&
    (!f.cars?.length || f.cars.includes(r.car ?? '')));
}

/** Số trên 4 tab chính + 3 tab "Thêm" (board `TABS`). */
export function tabCounts(d: {
  drafts: readonly BlDraft[]; finals: readonly BlFinal[]; releases: readonly BlRelease[];
  amends: readonly BlAmend[]; lois: readonly LoiTemplate[]; originals: readonly OriginalDoc[]; manifests: readonly Manifest[];
}): Record<BlTab, number> {
  return {
    draft: d.drafts.filter((r) => r.resp !== 'ok').length,
    final: d.finals.filter((r) => r.st !== 'sent').length,
    rel: d.releases.filter((r) => r.step < 5).length,
    amd: d.amends.filter((r) => r.st === 'wait').length,
    loi: d.lois.length,
    orig: d.originals.length,
    mnf: d.manifests.filter((r) => r.st === 'todo').length,
  };
}

export type FinalLeft = { kind: 'sent' } | { kind: 'overdue'; since: string } | { kind: 'left'; hours: number; minutes: number; soon: boolean };

/** Còn lại tới hạn gửi Final (giờ làm việc đã quy sẵn thành phút trong `dueM`; `now` = phút trong ngày). */
export function finalLeft(r: BlFinal, now: number): FinalLeft {
  if (r.st === 'sent') return { kind: 'sent' };
  const left = r.dueM - now;
  if (r.dueM < 0 || left < 0) return { kind: 'overdue', since: r.due.split(' ')[0]!.slice(0, 6) };
  return { kind: 'left', hours: Math.floor(left / 60), minutes: left % 60, soon: left < 60 };
}

/** Tên bước hiện tại của một yêu cầu release. */
export function releaseStepLabel(r: BlRelease, steps: readonly string[]): string {
  if (r.step >= 5) return 'Đã gửi Release Confirmation';
  if (r.rej) return 'Bị từ chối · trả CS';
  return steps[r.step] ?? '';
}

/** Yêu cầu release đang chờ người xem duyệt (hàng "Duyệt" ở dock). */
export const pendingMyApproval = (rows: readonly BlRelease[]): BlRelease[] => rows.filter((r) => r.mine && r.step === 2 && !r.rej);
